import type React from 'react';
import {useState} from 'react';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField} from '@mui/material';
import {errorText} from '../../hooks/notices';
import type {ExternalSignals} from '../../types/signalsTypes';

type Props = {
    category: string;
    mapping: ExternalSignals['mapping'];
    onConfirm: (node: string, code: string) => Promise<unknown>;
    onClose: () => void;
};

/** Review the external taxonomy node for the category and confirm it (spec 4.12 "Review mapping"). */
export const MappingDialog: React.FC<Props> = ({category, mapping, onConfirm, onClose}) => {
    const [node, setNode] = useState(mapping.node ?? mapping.suggestion);
    const [code, setCode] = useState(mapping.code ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const confirm = () => {
        setBusy(true);
        onConfirm(node.trim(), code.trim()).then(onClose).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };
    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-mapping-title">
            <DialogTitle id="cp-mapping-title">Review mapping</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Alert severity="info" icon={false}>
                        {`Signals and category risk come from the provider's taxonomy. Pick the node that matches “${category}”.`}
                    </Alert>
                    <TextField label="External taxonomy node" size="small" value={node} autoFocus
                               onChange={e => setNode(e.target.value)} slotProps={{htmlInput: {maxLength: 300}}}/>
                    <TextField label="Code" size="small" value={code} onChange={e => setCode(e.target.value)}
                               slotProps={{htmlInput: {maxLength: 60}}} helperText="The node's code in the provider taxonomy, if known"/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={confirm} disabled={busy || !node.trim()}>Confirm mapping</Button>
            </DialogActions>
        </Dialog>
    );
};
